import { SkyBadge, SkyButton, SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import { useState, type ReactNode } from "react";
import type { RunEventOrder, RunEventRow } from "@shared/types/runs";
import { failureMessage } from "@/lib/api";
import {
  eventSentence,
  eventStep,
  secretVersionLines,
  type EventScope,
} from "@/lib/runs/eventSentences";
import { RunEventFilters } from "./RunEventFilters";
import { TechnicalDetailsButton, hasTechnicalHandle } from "./TechnicalDetailsDialog";
import { STEP_LABELS } from "./runFormatting";
import { formatInstant, instantHoverTitle } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * The run's event log (FR-56, NFR-6; Session 10, P11: readable sentences, server filters).
 *
 * Cursor paginated like the history, and for the same reason: a long run writes thousands of
 * events. Round 4 (item 10c): the host's `CursorPager` (Previous, Next, 50/100/200 a page,
 * "1-50 of N") goes under the list as `pager`; Next reads the page before it moves, so a failed
 * page leaves every event already on screen exactly where it was, because losing a log the user
 * was reading mid-incident is worse than the missing page.
 *
 * Each row reads as a sentence (`lib/runs/eventSentences.ts`): the time in the reader's zone
 * (its exact time, zone named, in its `title`, #1078), the level as a word and a colour, the step, and what happened. The raw
 * code and the payload stay behind "Show detail" for support, rendered as formatted JSON rather
 * than parsed into fields: the payload's shape varies by event, and a renderer that guessed at its
 * keys would quietly drop the one that mattered.
 *
 * Every filter is the server's (the page's `RunEventFilters`); none is applied to the pages
 * already loaded, so "Error" finds an error on page 20.
 */

const LEVEL_COLORS = { error: "danger", warn: "warning", info: "informative" } as const;
const LEVEL_WORDS: Record<string, string> = { error: "Error", warn: "Warning", info: "Info" };

type Level = "info" | "warn" | "error";

export interface RunEventsLogProps {
  events: RunEventRow[];
  isLoading: boolean;
  /** A page that could not be read; the events on screen stay. */
  error: Error | null;
  /** The error bar's title: the next page's failure or this page's. */
  errorTitle?: string;
  onRetry: () => void;
  /** Round 4 (10c): the host's `CursorPager`, under the list. */
  pager?: ReactNode;
  /** Where this page sits, "1-50 of 312", when the server says the total. */
  position?: string | null;
  /** The Level filter alone, for a host with no filter bar of its own. */
  level?: Level;
  onLevelChange?: (level: Level | undefined) => void;
  /** The run page's full filter bar (`RunEventFilters`); replaces the Level-only one. */
  filterBar?: ReactNode;
  /** The order the rows arrive in; the list says it. */
  order?: RunEventOrder;
  /** True when a filter narrows the log: the empty state then says so and offers to clear. */
  filtered?: boolean;
  onClearFilters?: () => void;
  /** Names each row's connection, for a run of more than one. */
  connectionNames?: ReadonlyMap<string, string>;
  /** #988: a dbt jobs only run's events say "transformation run", not "import". */
  scope?: EventScope;
}

/** The instant in the reader's display zone, and to the second with the zone named for the `title`. */
function eventTimes(iso: string, zone: string): { local: string; hover: string | undefined } {
  return { local: formatInstant(iso, zone, "seconds"), hover: instantHoverTitle(iso, zone) };
}

/** The log with no event: none matches the filters (with Clear filters), or none yet. */
function emptyEventsBar(filtered: boolean, onClearFilters: (() => void) | undefined): ReactNode {
  if (filtered) {
    return (
      <SkyMessageBar
        intent="info"
        layout="multiline"
        title="No events match these filters."
        message="Clear the filters to see every event of this import."
        actions={
          onClearFilters ? (
            <SkyButton size="small" onClick={onClearFilters} testId="run-events-clear">
              Clear filters
            </SkyButton>
          ) : undefined
        }
        testId="run-events-filtered-empty"
      />
    );
  }
  return (
    <SkyMessageBar
      intent="info"
      layout="multiline"
      title="No events yet"
      message="The runner writes an event as each step and entity reports. They appear here as they arrive."
      testId="run-events-empty"
    />
  );
}

function SecretVersionsDetail({ event }: { event: RunEventRow }) {
  if (event.event !== "SECRET_VERSIONS_USED") return null;
  return (
    <>
      <ul className="list-disc pl-5 text-sm" data-testid={`run-event-secret-versions-${event.id}`}>
        {secretVersionLines(event.payload).map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {event.payload ? (
        <SkyText size={200} color="muted" block>
          Raw record. The values are Key Vault version ids, not credentials.
        </SkyText>
      ) : null}
    </>
  );
}

export function RunEventsLog({
  events,
  isLoading,
  error,
  errorTitle = "We could not load more events",
  onRetry,
  pager,
  position,
  level,
  onLevelChange,
  filterBar,
  order = "newest",
  filtered = false,
  onClearFilters,
  connectionNames,
  scope,
}: RunEventsLogProps) {
  const zone = useDisplayTz();
  const [expanded, setExpanded] = useState<string | null>(null);
  const orderWords = order === "oldest" ? "oldest first" : "newest first";
  const namesConnections = (connectionNames?.size ?? 0) > 1;
  const positionNote = position ? ` (${position})` : "";

  return (
    <div data-testid="run-events-log">
      {filterBar ??
        (onLevelChange ? (
          <RunEventFilters value={{ level }} onChange={(next) => onLevelChange(next.level)} />
        ) : null)}
      <SkyText size={200} color="muted" aria-live="polite" block className="mb-2">
        {isLoading
          ? "Loading events…"
          : `${events.length} events shown${positionNote}, ${orderWords}`}
      </SkyText>

      {error ? (
        <div className="mb-3">
          <SkyMessageBar
            intent="error"
            layout="multiline"
            title={errorTitle}
            message={`${failureMessage(error, "Try again.")} The events already listed are unchanged.`}
            actions={
              <SkyButton size="small" onClick={onRetry}>
                Try again
              </SkyButton>
            }
            testId="run-events-error"
          />
        </div>
      ) : null}

      {events.length === 0 && !isLoading && !error ? (
        emptyEventsBar(filtered, onClearFilters)
      ) : (
        <ol className="flex flex-col gap-1" aria-label={`Run events, ${orderWords}`}>
          {events.map((event) => {
            const color = LEVEL_COLORS[event.level as keyof typeof LEVEL_COLORS] ?? "subtle";
            const isOpen = expanded === event.id;
            const step = eventStep(event);
            const times = eventTimes(event.at, zone);
            const connection =
              namesConnections && event.dataflowId
                ? (connectionNames?.get(event.dataflowId) ?? null)
                : null;
            return (
              <li
                key={event.id}
                className="rounded-md border border-border px-3 py-2"
                data-testid={`run-event-${event.id}`}
                data-event={event.event}
              >
                <div className="flex flex-wrap items-center gap-3">
                  <SkyText size={200} color="muted" font="numeric" className="w-44 shrink-0">
                    <time dateTime={event.at} title={times.hover}>
                      {times.local}
                    </time>
                  </SkyText>
                  <SkyBadge appearance="tint" color={color}>
                    {LEVEL_WORDS[event.level] ?? event.level}
                  </SkyBadge>
                  <SkyText size={200} color="muted" className="w-20 shrink-0">
                    {step ? STEP_LABELS[step] : "Run"}
                  </SkyText>
                  <SkyText
                    size={300}
                    className="min-w-0 flex-1"
                    testId={`run-event-sentence-${event.id}`}
                  >
                    {eventSentence(event, scope)}
                  </SkyText>
                  {connection ? (
                    <SkyText size={200} color="muted">
                      {connection}
                    </SkyText>
                  ) : null}
                  <SkyButton
                    appearance="subtle"
                    size="small"
                    aria-expanded={isOpen}
                    onClick={() => setExpanded(isOpen ? null : event.id)}
                  >
                    {isOpen ? "Hide detail" : "Show detail"}
                  </SkyButton>
                </div>
                {isOpen ? (
                  <div className="mt-2 flex flex-col gap-1">
                    <SkyText size={200} color="muted" font="monospace" block>
                      {event.entityName ? `${event.event} · ${event.entityName}` : event.event}
                    </SkyText>
                    <SecretVersionsDetail event={event} />
                    {event.payload ? (
                      <pre className="max-h-64 overflow-auto rounded-md bg-muted p-2 text-xs">
                        {JSON.stringify(event.payload, null, 2)}
                      </pre>
                    ) : null}
                    {/* R2.4: the raw text behind this row, for audit readers only. */}
                    {hasTechnicalHandle(event.payload) ? (
                      <div>
                        <TechnicalDetailsButton
                          runId={event.runId}
                          eventId={event.id}
                          testId={`run-event-technical-${event.id}`}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}

      {pager ?? null}
    </div>
  );
}
